'use client';

import { useCallback, useEffect, useState } from 'react';
import { FileTextIcon, MapPinIcon } from '@phosphor-icons/react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useProfile } from '@/hooks/queries/useProfile';
import { formatCep } from './billingAddress.helpers';
import { BillingAddressForm } from './BillingAddressForm';

/**
 * The "Endereço de cobrança" block on Perfil (VER-389). The nota fiscal is always
 * issued to the account holder, so the only document surface is a read-only row —
 * rendered as a detail, not a disabled input a user would try to edit.
 */
export function BillingAddressSection({
  blockedPlanName,
  onSaved,
}: {
  /** Set by the Assinar pre-flight — renders the "falta o endereço" alert and opens the form. */
  blockedPlanName: string | null;
  onSaved: () => void;
}) {
  const { data: profile } = useProfile();
  const [editing, setEditing] = useState(false);

  const address = profile?.billingAddress ?? null;
  const preFlight = blockedPlanName !== null;

  useEffect(() => {
    if (preFlight) setEditing(true);
  }, [preFlight]);

  const handleStartEditing = useCallback(() => setEditing(true), []);
  const handleCancel = useCallback(() => setEditing(false), []);
  const handleSaved = useCallback(() => {
    setEditing(false);
    onSaved();
  }, [onSaved]);

  if (profile === undefined) return null;

  const formOpen = editing || (address === null && preFlight);

  return (
    <section id="dados-de-cobranca" className="scroll-mt-24">
      <div className="mb-4">
        <p className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
          Endereço de cobrança
        </p>
        <p className="mt-1 text-sm text-text-secondary">
          Endereço usado na nota fiscal da sua assinatura.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <dl className="flex min-w-0 gap-3">
          <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-surface-sunken text-brand-primary">
            <FileTextIcon className="size-4" weight="regular" />
          </div>
          <div className="min-w-0">
            <dt className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
              Nota fiscal emitida para
            </dt>
            <dd className="mt-0.5 text-sm font-medium text-text-primary">
              {profile.cpfMasked ?? 'CPF não cadastrado'}
            </dd>
            <dd className="mt-0.5 text-xs text-text-tertiary">
              A nota fiscal é sempre emitida para o titular da conta.
            </dd>
          </div>
        </dl>

        {preFlight && (
          <Alert variant="info" role="status">
            Falta o endereço de cobrança para assinar o {blockedPlanName}. É rápido: comece pelo CEP.
          </Alert>
        )}

        {formOpen ? (
          <BillingAddressForm
            address={address}
            autoFocusCep={preFlight}
            onSaved={handleSaved}
            onCancel={preFlight && address === null ? undefined : handleCancel}
          />
        ) : address === null ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-text-secondary">
              Você ainda não cadastrou seu endereço de cobrança. Precisamos dele para emitir a nota
              fiscal quando você assinar.
            </p>
            <Button variant="outline" onClick={handleStartEditing} className="cursor-pointer">
              Cadastrar endereço
            </Button>
          </div>
        ) : (
          <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-surface-sunken text-brand-primary">
              <MapPinIcon className="size-4" weight="regular" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-text-primary">
                {address.logradouro}, {address.numero}
                {address.complemento ? ` · ${address.complemento}` : ''}
              </p>
              <p className="mt-0.5 text-sm text-text-secondary">
                {address.bairro}, {address.cidade}/{address.uf} — CEP {formatCep(address.cep)}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleStartEditing} className="shrink-0 cursor-pointer">
              Editar
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
